import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CreateParentData, ParentsFilters, UpdateParentData, parentsApi } from '@/services';

export const adminParentsKeys = {
  all: ['admin', 'parents'] as const,
  list: (filters: ParentsFilters = {}) => ['admin', 'parents', 'list', filters] as const,
  detail: (id: string) => ['admin', 'parents', 'detail', id] as const,
  byStudent: (studentId: string) => ['admin', 'parents', 'by-student', studentId] as const,
};

export function useAdminParentsListQuery(filters: ParentsFilters = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: adminParentsKeys.list(filters),
    queryFn: () => parentsApi.list(filters),
    enabled: options?.enabled,
  });
}

export function useAdminParentQuery(id: string | undefined) {
  return useQuery({
    queryKey: adminParentsKeys.detail(id ?? ''),
    queryFn: () => parentsApi.getById(id!),
    enabled: Boolean(id),
  });
}

export function useStudentParentsQuery(studentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: adminParentsKeys.byStudent(studentId ?? ''),
    queryFn: () => parentsApi.list({ studentId: studentId!, limit: 500 }).then(r => r.data),
    enabled: Boolean(studentId) && enabled,
  });
}

export function useLinkStudentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ parentId, studentId }: { parentId: string; studentId: string }) =>
      parentsApi.linkStudent(parentId, studentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminParentsKeys.all }),
  });
}

export function useUnlinkStudentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ parentId, studentId }: { parentId: string; studentId: string }) =>
      parentsApi.unlinkStudent(parentId, studentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminParentsKeys.all }),
  });
}

export function useCreateParentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateParentData) => parentsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminParentsKeys.all }),
  });
}

export function useUpdateParentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateParentData }) => parentsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminParentsKeys.all }),
  });
}

export function useResendParentInviteMutation() {
  return useMutation({ mutationFn: (id: string) => parentsApi.resendInvite(id) });
}

export function useUpdateParentActiveMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => parentsApi.updateActive(id, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminParentsKeys.all }),
  });
}
